import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from '../components/icons';
import { ProgressBar } from '../components/ProgressBar';
import type { Question } from '../types';
import type { HarmonicQuestion } from '../types/harmonic';

interface Props {
  currentQuestion: number;
  totalQuestions: number;
  question: Question | HarmonicQuestion;
  isHarmonic: boolean;
  handleAnswer: (questionId: string, value: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  currentAnswer: number | undefined;
  canGoBack: boolean;
  labels?: {
    scaleOptions?: { value: number; label: string }[];
    progressQuestion?: string;
    progressOf?: string;
    progressComplete?: string;
    scaleMin?: string;
    scaleMax?: string;
    previousQuestion?: string;
    backToInstructions?: string;
  };
}

const defaultScaleLabels = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Slightly Disagree' },
  { value: 4, label: 'Neutral' },
  { value: 5, label: 'Slightly Agree' },
  { value: 6, label: 'Agree' },
  { value: 7, label: 'Strongly Agree' }
];

export function QuestionnaireView({
  currentQuestion,
  totalQuestions,
  question,
  isHarmonic,
  handleAnswer,
  goToNext,
  goToPrevious,
  currentAnswer,
  canGoBack,
  labels
}: Props) {
  const navigate = useNavigate();
  const scaleLabels = labels?.scaleOptions ?? defaultScaleLabels;
  const scaleMin = labels?.scaleMin ?? '1 = Strongly Disagree';
  const scaleMax = labels?.scaleMax ?? '7 = Strongly Agree';
  const previousQuestion = labels?.previousQuestion ?? 'Previous Question';
  const backToInstructions = labels?.backToInstructions ?? 'Back to Instructions';

  const handleSelectValue = (value: number) => {
    handleAnswer(question.id, value);
    setTimeout(() => {
      goToNext();
    }, 300);
  };

  const handleBack = () => {
    if (canGoBack) {
      goToPrevious();
    } else {
      navigate('/instructions');
    }
  };

  return (
    <div className="min-h-screen freud-bg flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-sage-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-sand-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cream-300/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8 w-full flex-1 flex flex-col">
        <div className="mb-10 bg-white/70 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-cream-200">
          <ProgressBar
            current={currentQuestion}
            total={totalQuestions}
            questionLabel={labels?.progressQuestion}
            ofLabel={labels?.progressOf}
            completeLabel={labels?.progressComplete}
          />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="w-full"
            >
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-cream-200 p-8">
                <p className="freud-heading-lg text-chocolate-800 text-center mb-10 leading-relaxed">
                  {question.text}
                </p>

                <div className="grid grid-cols-7 gap-2 md:gap-3">
                  {scaleLabels.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => handleSelectValue(value)}
                      className={`
                        flex flex-col items-center px-1 py-3 rounded-xl transition-all duration-200 min-h-[80px]
                        ${currentAnswer === value
                          ? 'bg-gradient-to-br from-coral-500 to-coral-600 text-white shadow-lg scale-105 border-2 border-coral-400'
                          : 'bg-cream-50 hover:bg-sage-100 text-chocolate-700 border border-cream-200 hover:border-sage-300 hover:scale-102'
                        }
                      `}
                    >
                      <span className="freud-heading-xl mb-1">{value}</span>
                      <span className={`text-[10px] text-center leading-tight break-words w-full hidden md:block ${
                        currentAnswer === value ? 'text-white/90' : 'text-chocolate-500'
                      }`}>
                        {label}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="flex justify-center gap-8 mt-6">
                  <span className="freud-caption px-3 py-1 bg-sage-100 rounded-full text-chocolate-500">{scaleMin}</span>
                  <span className="freud-caption px-3 py-1 bg-sage-100 rounded-full text-chocolate-500">{scaleMax}</span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-chocolate-600 hover:bg-white/70 hover:shadow-sm transition-all border border-transparent hover:border-cream-200"
          >
            <ChevronLeft className="w-5 h-5" />
            {canGoBack ? previousQuestion : backToInstructions}
          </button>
        </div>
      </div>
    </div>
  );
}
